import { authorizedSignatorySchema, facilitySchema } from "@src/appV2/Facilities/types";
import { TimeSlot } from "@src/appV2/redesign/Shift/Open/types";
import { z } from "zod";

export enum ShiftPeriod {
  AM = "am",
  PM = "pm",
  NOC = "noc",
}

export enum ShiftGeofenceStatusType {
  ENTRY = "entry",
  EXIT = "exit",
  START_BREAK = "start-break",
  END_BREAK = "end-break",
}

export const shiftPeriodToTimeSlot: Record<ShiftPeriod, TimeSlot> = {
  [ShiftPeriod.AM]: TimeSlot.AM,
  [ShiftPeriod.PM]: TimeSlot.PM,
  [ShiftPeriod.NOC]: TimeSlot.NOC,
};

export const timeSlotToShiftPeriod: Record<TimeSlot, ShiftPeriod> = {
  [TimeSlot.AM]: ShiftPeriod.AM,
  [TimeSlot.PM]: ShiftPeriod.PM,
  [TimeSlot.NOC]: ShiftPeriod.NOC,
};

export enum UrgencyType {
  NCNS = 1,
  LATE_CANCELLATION = 2,
  LAST_MINUTE = 3,
  /**
   * The server has `UrgentShiftUrgencyType` for the above three, but we see 4 for some responses
   * on `api/v1/shifts/myShifts/nextTwoDays` responses, which come directly from the db.
   */
  UNKNOWN = 4,
}

export enum ShiftWindow {
  CLIPBOARD_SCORE_FIRST = "CLIPBOARD_SCORE_FIRST",
  CLIPBOARD_SCORE_SECOND = "CLIPBOARD_SCORE_SECOND",
  FAMILIAR_WORKERS = "FAMILIAR_WORKERS",
  RELIABLE_AND_FAMILIAR = "RELIABLE_AND_FAMILIAR",
}

export enum NfcTagRequestStatus {
  REQUESTED = "REQUESTED",
  ON_ITS_WAY = "ON_ITS_WAY",
  DELIVERED = "DELIVERED",
  SET_UP = "SET_UP",
  DELETED = "DELETED",
}

const nfcTagRequestSchema = z.object({
  tagStatus: z.nativeEnum(NfcTagRequestStatus),
  tagLocation: z.string().optional(),
});

export enum InstantPayPaymentVia {
  STRIPE = "STRIPE",
  DAILY_PAY = "DAILY_PAY",
}

export enum ShiftSpecificationCategory {
  GRADE_AND_SUBJECT = "GRADE_AND_SUBJECT",
  AGE_GROUP = "AGE_GROUP",
  PATIENT_SCHEDULE = "PATIENT_SCHEDULE",
  DRIVE_ROUTE = "DRIVE_ROUTE",
  ADDITIONAL_INFO = "ADDITIONAL_INFO",
}

export type NfcTagRequest = z.infer<typeof nfcTagRequestSchema>;

const instantPayShiftDetailsSchema = z.object({
  totalAmount: z.number().optional(),
  paidAmount: z.number().optional(),
  is100InstantPayEnabled: z.boolean().optional(),
});

export type InstantPayShiftDetails = z.infer<typeof instantPayShiftDetailsSchema>;

export const timecardSchema = z.object({
  createdAt: z.string().optional(),
  files: z.array(
    z.object({
      _id: z.string(),
      url: z.string().optional(),
      signedType: z.string().nullish(),
      timesheet: z.string(),
      fileStorageFileKey: z.string(),
      filename: z.string(),
      fileStorageRootFolder: z.string().nullish(),
    })
  ),
  status: z.string().optional(),
});
export type Timecard = z.infer<typeof timecardSchema>;

const timeRangeSchema = z.object({
  start: z.string().nullish(),
  end: z.string().nullish(),
  startCard: z.string().nullish(),
  endCard: z.string().nullish(),
  isSkipped: z.boolean().nullish(),
  startImage: z.string().nullish(),
  endImage: z.string().nullish(),
});

const geofenceSchema = z.object({
  entry: z.string().nullish(),
  exit: z.string().nullish(),
  startBreak: z.string().nullish(),
  endBreak: z.string().nullish(),
  status: z.nativeEnum(ShiftGeofenceStatusType),
});

export const shiftSchema = z.object({
  _id: z.string(),
  start: z.string(),
  end: z.string(),
  agentReq: z.string(),
  name: z.nativeEnum(ShiftPeriod),
  time: z.number().optional(),
  pay: z.number().optional(),
  distance: z.number().optional(),
  isApproxDistance: z.boolean().optional(),
  charge: z.number().optional(),
  note: z.string().optional(),
  description: z.string().nullish(),
  geofence: geofenceSchema.nullish(),
  surgerySpeciality: z
    .object({
      hasSedationExperience: z.boolean(),
      hasTrayAssemblyExperience: z.boolean(),
      speciality: z.string().nullish(),
    })
    .optional(),
  facilityId: z.string().optional(),
  facility: facilitySchema,
  agentId: z.string().nullish(),
  interested: z.array(z.string()).optional(),
  signed: z.boolean().optional(),
  verified: z.boolean().optional(),
  signatory: authorizedSignatorySchema.nullish(),
  deleted: z.boolean().nullish(),
  clockInOut: timeRangeSchema.nullish(),
  lunchInOut: timeRangeSchema.nullish(),
  unit: z.string().nullish(),
  timecard: timecardSchema.optional(),
  instantPayDetails: instantPayShiftDetailsSchema.nullish(),
  isHolidayShift: z.boolean().optional(),
  isChangedToNonInstantPay: z.boolean().optional(),
  timecardNotAvailable: z
    .object({
      reason: z.string(),
      setBy: z.string(),
      updatedAt: z.string(),
    })
    .optional(),
  autoClockedOut: z.boolean().optional(),
  holidayName: z.string().nullish(),
  isHCFInstantPayProhibited: z.boolean().nullish(),
  isInstantPay: z.boolean().optional(),
  urgency: z.nativeEnum(UrgencyType).nullish(),
  urgentlyBooked: z.boolean().nullish(),
  priorityTill: z.string().nullish(),
  /**
   * payBreakDown and chargeBreakDown are deprecated
   * See: https://clipboardhealth.atlassian.net/browse/CH-22416
   * Use `isChargeUpdatedManually` instead.
   */
  isChargeUpdatedManually: z.boolean().optional(),
  facilityNotes: z
    .array(
      z.object({
        category: z.string(),
        identifier: z.string(),
        note: z.string(),
        noteId: z.string(),
        updatedAt: z.string(),
      })
    )
    .optional(),
  finalPay: z.number().optional(),
  originalAmount: z.number().nullish(),
  shiftId: z.string().optional(),
  isInstantBook: z.boolean().optional(),
  isInstantForAgent: z.boolean().optional(),
  isStripePayment: z.boolean().nullish(),
  nfcTag: z.boolean().nullish(),
  nfcTagRequests: z.array(nfcTagRequestSchema).nullish(),
  offer: z
    .object({
      id: z.string(),
      pay: z.object({
        value: z.number(),
        currency: z.string(),
      }),
      expiresAt: z.string().optional(),
    })
    .nullish(),
  shiftBlockId: z.string().nullish(),
  orientationBlockId: z.string().nullish(),
  latestShiftClaimLog: z
    .object({
      shift: z.object({
        workerId: z.string(),
      }),
      eventAt: z.string(),
    })
    .optional(),
  cancellationWithoutPenaltyThresholdTime: z.string().optional(),
  shiftSpecifications: z
    .array(
      z.object({
        category: z.nativeEnum(ShiftSpecificationCategory),
        requirement: z.string(),
      })
    )
    .optional(),
  window: z.nativeEnum(ShiftWindow).nullish(),
  isSecondShiftInDouble: z.boolean().optional(),
  isAfterAnotherShift: z.boolean().optional(),
  isBeforeAnotherShift: z.boolean().optional(),
  bookedAt: z.string().optional(),
  computedArrival: z
    .object({
      workerId: z.string(),
      durationInMinutes: z.number(),
      distanceInMeters: z.number(),
      bookedAt: z.string().datetime(),
      shiftStartAt: z.string().datetime(),
      targetArrivalTime: z.string().datetime(),
      gracePeriodInMins: z.number(),
      provider: z.enum(["GOOGLE_MAPS", "AWS_LOCATION", "SHIFT_START"]),
    })
    .optional(),
  cancellationProtection: z
    .object({
      applied: z.boolean(),
    })
    .optional(),
  attendance: z
    .object({
      arrivedFacilityGeofenceAt: z.string().optional(),
      leftFacilityGeofence: z.boolean().optional(),
      leftFacilityGeofenceAt: z.string().optional(),
      isWorkerFarFromFacility: z.boolean().optional(),
      earliestPlausibleArrivalAt: z.string().optional(),
      latestPlausibleDepartureAt: z.string().optional(),
    })
    .nullable()
    .optional(),
});

export const workerShiftSchema = z
  .object({
    _id: z.string(),
    start: z.string(),
    end: z.string(),
    name: z.nativeEnum(ShiftPeriod),
    agentReq: z.string(),
    agentId: z.string(),
    agent: z
      .object({
        name: z.string(),
      })
      .optional(),
    facilityId: z.string(),
    isInstantPay: z.boolean(),
    isChangedToNonInstantPay: z.boolean(),
    isTransferrable: z.boolean().optional(),
    instantPayDetails: instantPayShiftDetailsSchema.optional(),
  })
  .merge(
    shiftSchema.pick({
      verified: true,
      deleted: true,
      clockInOut: true,
      lunchInOut: true,
      timecard: true,
      timecardNotAvailable: true,
      facility: true,
      nfcTag: true,
      nfcTagRequests: true,
      offer: true,
      finalPay: true,
      originalAmount: true,
      geofence: true,
      isSecondShiftInDouble: true,
      isAfterAnotherShift: true,
      isBeforeAnotherShift: true,
      time: true,
      instantPayDetails: true,
      urgentlyBooked: true,
      bookedAt: true,
      computedArrival: true,
      shiftSpecifications: true,
      orientationBlockId: true,
      attendance: true,
      cancellationProtection: true,
    })
  );
export type WorkerShift = z.infer<typeof workerShiftSchema>;

export type Shift = z.infer<typeof shiftSchema>;

const shiftSelfCancelErrorSchema = z.object({
  status: z.string(),
  detail: z.string(),
  title: z.string(),
  code: z.string().nullish(),
  id: z.string(),
});

export const selfCancelShiftErrorResponseSchema = z.object({
  errors: z.array(shiftSelfCancelErrorSchema),
});

export type SelfCancelShiftErrorResponse = z.infer<typeof selfCancelShiftErrorResponseSchema>;

export enum RateNegotiationStatus {
  OPEN = "OPEN",
  EXPIRED = "EXPIRED",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  CLOSED = "CLOSED",
}

export type TimeRange = z.infer<typeof timeRangeSchema>;

export enum ExtraWorkedTimeType {
  BREAK = "BREAK",
  FULL_SHIFT = "FULL_SHIFT",
}
